import { useState } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import cx from 'classnames';

import { PaymentType } from 'types';
import { displayPrice, getStripeReturnURL } from 'utils';
import { Loader } from 'components';
import { Spinner } from 'components/Icon';

import styles from './Payment.module.scss';

export type PaymentFormType = {
  price: number;
  pricingPlanId: string;
  paymentType: PaymentType;
};

const PaymentForm = ({ price, pricingPlanId, paymentType }: PaymentFormType) => {
  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState<string>('');
  const [isSubmitting, setSubmitting] = useState(false);
  const [isStripeLoading, setStripeLoading] = useState(true);
  const isStripeReady = !isStripeLoading && stripe && elements;

  const formattedPrice = displayPrice(price);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      return;
    }

    setSubmitting(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: getStripeReturnURL(pricingPlanId, paymentType)
      }
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment
    if (error.type === 'card_error' || error.type === 'validation_error') {
      setMessage(error?.message || '');
    } else {
      setMessage('An unexpected error occured.');
    }

    setSubmitting(false);
  };

  return (
    <div className={styles.form}>
      {isStripeLoading && <Loader global />}

      <form id="payment-form" onSubmit={handleSubmit}>
        <PaymentElement className={styles.content} id="payment-element" onReady={() => setStripeLoading(false)} />

        {isStripeReady && (
          <button type="submit" disabled={isSubmitting} className={cx({ [styles.active]: !message })}>
            {isSubmitting ? <Spinner /> : `Pay $${formattedPrice}`}
          </button>
        )}

        {message && <span className={cx(styles.feedback, styles.error)}>{message}</span>}
      </form>
    </div>
  );
};

export default PaymentForm;
